import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, Observable, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { Router } from '@angular/router';
import { Notifications } from './notifications';

export interface HttpOptions {
  headers?: HttpHeaders;
  params?: HttpParams;
}

@Injectable({
  providedIn: 'root',
})
export class Api {
  private http = inject(HttpClient);
  private router = inject(Router);
  private notification = inject(Notifications);
  private baseApiUrl = environment.apiUrl;

  constructor() {}

  // Mètode per gestionar errors (opcional, es pot fer amb interceptors també)
  private formatError(error: any): Observable<never> {
    let errorMessage = 'Se ha producido un error desconocido.';
    if (error.error instanceof ErrorEvent) {
      // Error de client-side o de xarxa
      errorMessage = `Error del cliente: ${error.error.message}`;
    } else if (error.status) {
      // Error de backend
      errorMessage = `Error del servidor: ${error.status} - ${error.statusText || ''}`;
      if (error.error && typeof error.error === 'object' && error.error.message) {
        errorMessage = `${errorMessage}\nMensaje: ${error.error.message}`;
      }
      if (error.status === 401) {
        // Aquí podríem redirigir a la pàgina de login si és necessari
        this.notification.showError(
          'No tienes permiso para acceder a este recurso. Por favor, inicia sesión.'
        );
      }
    }

    return throwError(() => new Error(errorMessage));
  }

  getFullUrl(path: string, params: any): string {
    let url = `${this.baseApiUrl}${path}`;
    if (params) {
      const queryParams = new HttpParams({ fromObject: params });
      url += `?${queryParams.toString()}`;
    }
    return url;
  }

  /**
   * Realitza una petició GET.
   * @param path El camí relatiu a la URL base de l'API.
   * @param options Opcions HTTP (headers, params).
   */
  get<T>(path: string, options: HttpOptions = {}): Observable<T> {
    return this.http
      .get<T>(`${this.baseApiUrl}${path}`, options)
      .pipe(catchError(this.formatError.bind(this)));
  }

  /**
   * Descàrrega d'un fitxer JSON com a Blob (per exemple per forçar descàrrega).
   * @param path El camí relatiu a la URL base de l'API.
   * @param options Opcions HTTP (headers, params).
   */
  getJsonFile(path: string, options: HttpOptions = {}): Observable<ArrayBuffer> {
    // Construïm les opcions de la petició amb responseType 'blob'
    const requestOptions: any = {
      ...(options as any),
      responseType: 'blob' as 'blob',
    };

    // Assegurem l'header Accept perquè el servidor retorni JSON
    if (!requestOptions.headers) {
      requestOptions.headers = new HttpHeaders({ Accept: 'application/json' });
    } else if (!requestOptions.headers.has || !requestOptions.headers.has('Accept')) {
      // HttpHeaders.set retorna una nova instància
      requestOptions.headers = requestOptions.headers.set
        ? requestOptions.headers.set('Accept', 'application/json')
        : new HttpHeaders({ Accept: 'application/json' });
    }

    return this.http
      .get(`${this.baseApiUrl}${path}`, requestOptions)
      .pipe(catchError(this.formatError.bind(this)));
  }

  /**
   * Realitza una petició POST.
   * @param path El camí relatiu a la URL base de l'API.
   * @param body El cos de la petició.
   * @param options Opcions HTTP (headers, params).
   */
  post<T>(path: string, body: Object = {}, options: HttpOptions = {}): Observable<T> {
    return this.http
      .post<T>(`${this.baseApiUrl}${path}`, body, options)
      .pipe(catchError(this.formatError.bind(this)));
  }

  /**
   * Realitza una petició PUT.
   * @param path El camí relatiu a la URL base de l'API.
   * @param body El cos de la petició.
   * @param options Opcions HTTP (headers, params).
   */
  put<T>(path: string, body: Object = {}, options: HttpOptions = {}): Observable<T> {
    return this.http
      .put<T>(`${this.baseApiUrl}${path}`, body, options)
      .pipe(catchError(this.formatError.bind(this)));
  }

  /**
   * Realitza una petició PATCH.
   * @param path El camí relatiu a la URL base de l'API.
   * @param body El cos de la petició.
   * @param options Opcions HTTP (headers, params).
   */
  patch<T>(path: string, body: Object = {}, options: HttpOptions = {}): Observable<T> {
    return this.http
      .patch<T>(`${this.baseApiUrl}${path}`, body, options)
      .pipe(catchError(this.formatError.bind(this)));
  }

  /**
   * Realitza una petició DELETE.
   * @param path El camí relatiu a la URL base de l'API.
   * @param options Opcions HTTP (headers, params).
   */
  delete<T>(path: string, options: HttpOptions = {}): Observable<T> {
    return this.http
      .delete<T>(`${this.baseApiUrl}${path}`, options)
      .pipe(catchError(this.formatError.bind(this)));
  }
}
